/**
 * EmailDraftCard — Pre-filled email draft shown when the agent pauses for user input.
 *
 * Lets the agency user send a professional email to their customer requesting
 * the missing information needed for the quote. The email body is auto-generated
 * by the backend from the fields the agent is asking for.
 *
 * Used by both PLRChatView (Personal Lines) and CLRWorkflowPanel (Commercial Lines).
 * Styles live in RateQuoteLink.css under the .plr-edc__ namespace.
 */

import React, { useState, useRef, useCallback } from 'react';
import { FiMail, FiSend, FiChevronDown, FiChevronUp, FiCheck, FiX } from 'react-icons/fi';
import axios from 'axios';

export interface EmailDraft {
  subject: string;
  body: string;
  insured_name?: string;
  lob?: string;
  to_email?: string;
}

interface Props {
  emailDraft: EmailDraft;
  /** Base API path — used to build the send endpoint URL */
  apiBase?: string;
}

/** Replace {{agency_name}} placeholder with actual agency from localStorage */
function injectAgencyName(html: string): string {
  const agency = localStorage.getItem('agency') || '';
  return html.replace(/\{\{agency_name\}\}/g, agency || 'Your Insurance Team');
}

const EmailDraftCard: React.FC<Props> = ({ emailDraft, apiBase }) => {
  const [expanded, setExpanded] = useState(false);
  const [to, setTo] = useState(emailDraft.to_email || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(emailDraft.subject || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  // Initialize body with agency name injected
  const [bodyHtml] = useState(() => injectAgencyName(emailDraft.body || ''));

  const getBody = useCallback(() => {
    return bodyRef.current?.innerHTML || bodyHtml;
  }, [bodyHtml]);

  const handleSend = async () => {
    if (!to.trim()) {
      setError('Email address is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    setError('');
    setSending(true);
    try {
      const base = apiBase || process.env.REACT_APP_MyAMP_Url || '';
      const ncToken = localStorage.getItem('amsToken') || '';
      await axios.post(
        `${base}/api/ams/send-customer-email`,
        { to: to.trim(), cc: cc.trim(), subject, body: getBody(), ncToken },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setSent(true);
    } catch (err: any) {
      const msg = err?.response?.data?.errorMessage || 'Failed to send email';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="plr-edc">
        <div className="plr-edc__sent">
          <FiCheck size={14} />
          <span>Email sent to {to}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="plr-edc">
      {/* Header — always visible */}
      <button
        type="button"
        className="plr-edc__header"
        onClick={() => setExpanded(!expanded)}
      >
        <FiMail size={14} className="plr-edc__header-icon" />
        <span className="plr-edc__header-text">Email customer for missing info</span>
        {expanded
          ? <FiChevronUp size={14} className="plr-edc__header-chevron" />
          : <FiChevronDown size={14} className="plr-edc__header-chevron" />
        }
      </button>

      {/* Expanded form */}
      {expanded && (
        <div className="plr-edc__form">
          {/* To */}
          <div className="plr-edc__field">
            <label className="plr-edc__label">To <span className="plr-edc__req">*</span></label>
            <input
              type="email"
              value={to}
              onChange={e => { setTo(e.target.value); setError(''); }}
              placeholder="customer@email.com"
              className="plr-edc__input"
            />
          </div>

          {/* CC */}
          <div className="plr-edc__field">
            <label className="plr-edc__label">CC</label>
            <input
              type="email"
              value={cc}
              onChange={e => setCc(e.target.value)}
              placeholder="optional"
              className="plr-edc__input"
            />
          </div>

          {/* Subject */}
          <div className="plr-edc__field">
            <label className="plr-edc__label">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="plr-edc__input"
            />
          </div>

          {/* Body — fully editable */}
          <div className="plr-edc__field">
            <label className="plr-edc__label">Body</label>
            <div
              ref={bodyRef}
              className="plr-edc__body-preview"
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="plr-edc__error">
              <FiX size={12} /> {error}
            </div>
          )}

          {/* Send button */}
          <div className="plr-edc__actions">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="plr-edc__send"
            >
              {sending ? (
                <span>Sending...</span>
              ) : (
                <>
                  <FiSend size={12} />
                  <span>Send Email</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDraftCard;
